// @ts-check
'use strict';

/**
 * The condition report's derived content — counts, roll-ups, dashboard,
 * register, outlook, key risks, recommendation tiers — from one visit's areas.
 * Ported from Site2Scope's src/reportViewModel.js and recommendationLibrary.js,
 * generalised from Poor/Fair/Good to any rating scale through bands.
 * Pure: inputs are never mutated or reordered; sorting happens on copies.
 */

const { BAND_RANK, bandOf, ratingLabel } = require('./scales');
const { DEFECT_BY_ID, familyForSubstrate, generateConditionNote } = require('./phrases');
const {
  BAND_DEFAULT_DELIVERY, BAND_DEFAULT_PRIORITY, PRIORITY_RANK,
  DELIVERY_MODE_BY_KEY, BAND_RECOMMENDATION, OVERALL_THRESHOLDS,
} = require('./vocabulary');

/** @typedef {import('./scales').Band} Band */
/** @typedef {import('./vocabulary').Priority} Priority */
/** @typedef {import('./phrases').Side} Side */

/**
 * @typedef {object} Photo
 * @property {string} url
 * @property {boolean} [isPrimary]  shown first on the item's card
 */

/**
 * One rated thing in an area — a substrate or surface.
 *
 * @typedef {object} Item
 * @property {string} substrate
 * @property {string | null} [baseSurface]
 * @property {string | number | null} [rating]  a value on the visit's scale
 * @property {string[]} [defects]
 * @property {string[]} [customDefects]
 * @property {string | null} [extent]
 * @property {string | null} [note]             stored (possibly edited) note
 * @property {Photo[]} [photos]
 */

/**
 * @typedef {object} Area
 * @property {string} name
 * @property {Side | null} [side]
 * @property {string | null} [deliveryMode]     assessor's choice; else the band default
 * @property {Priority | null} [priority]       assessor's choice; else the band default
 * @property {number | null} [outlookYear]      when the delivery mode falls due
 * @property {Item[]} [items]
 */

/** @typedef {{poor: number, fair: number, good: number, total: number}} BandCounts */

/** @returns {BandCounts} */
const emptyCounts = () => ({ poor: 0, fair: 0, good: 0, total: 0 });

/**
 * @param {Item} item
 * @param {string} scale
 * @returns {Band | null}
 */
function itemBand(item, scale) {
  return bandOf(scale, item && item.rating);
}

/**
 * Rated items per band across all areas. An item with a note but no rating is
 * seen, not rated, and isn't counted.
 *
 * @param {Area[]} areas
 * @param {string} scale
 * @returns {BandCounts}
 */
function computeStats(areas, scale) {
  const stats = emptyCounts();
  for (const area of areas || []) {
    for (const item of (area && area.items) || []) {
      const band = itemBand(item, scale);
      if (!band) continue;
      stats[band] += 1;
      stats.total += 1;
    }
  }
  return stats;
}

/**
 * An area takes its worst item's band; null when nothing in it is rated.
 *
 * @param {Area} area
 * @param {string} scale
 * @returns {Band | null}
 */
function rollupAreaBand(area, scale) {
  /** @type {Band | null} */
  let worst = null;
  for (const item of (area && area.items) || []) {
    const band = itemBand(item, scale);
    if (band && (!worst || BAND_RANK[band] > BAND_RANK[worst])) worst = band;
  }
  return worst;
}

/**
 * Areas per band, by their roll-up.
 *
 * @param {Area[]} areas
 * @param {string} scale
 * @returns {BandCounts}
 */
function computeAreaStats(areas, scale) {
  const stats = emptyCounts();
  for (const area of areas || []) {
    const band = rollupAreaBand(area, scale);
    if (!band) continue;
    stats[band] += 1;
    stats.total += 1;
  }
  return stats;
}

/**
 * Site condition, proportional (see OVERALL_THRESHOLDS). An override wins.
 *
 * @param {BandCounts} stats
 * @param {Band | null} [override]
 * @returns {Band | null}
 */
function overallBand(stats, override) {
  if (override) return override;
  if (!stats || !stats.total) return null;
  if (stats.poor / stats.total >= OVERALL_THRESHOLDS.poorShare) return 'poor';
  if ((stats.poor + stats.fair) / stats.total >= OVERALL_THRESHOLDS.poorFairShare) return 'fair';
  return 'good';
}

/** Rated items not flagged for repaint (fair + good) — the report's "good news" line. */
/** @param {BandCounts} stats */
function itemsNotNeedingRepaint(stats) {
  return ((stats && stats.good) || 0) + ((stats && stats.fair) || 0);
}

/** @param {Band | null} band */
const defaultDeliveryMode = (band) => (band ? BAND_DEFAULT_DELIVERY[band] : null);
/** @param {Band | null} band */
const defaultPriority = (band) => (band ? BAND_DEFAULT_PRIORITY[band] : null);
/** @param {Area} area @param {Band | null} band */
const deliveryModeFor = (area, band) => (area && area.deliveryMode) || defaultDeliveryMode(band);
/** @param {Area} area @param {Band | null} band */
const priorityFor = (area, band) => (area && area.priority) || defaultPriority(band);
/** @param {string | null} key */
const deliveryLabel = (key) => (key && DELIVERY_MODE_BY_KEY[key] ? DELIVERY_MODE_BY_KEY[key].label : '');

/**
 * Rated areas only, each with its band — the input to every per-area builder.
 *
 * @param {Area[]} areas
 * @param {string} scale
 */
function ratedAreas(areas, scale) {
  return (areas || [])
    .map((area) => ({ area, band: rollupAreaBand(area, scale) }))
    .filter((x) => x.band !== null);
}

/**
 * Page 2: one row per rated area, highest priority first, then worst band.
 *
 * @param {Area[]} areas
 * @param {string} scale
 */
function buildDashboardRows(areas, scale) {
  const rows = ratedAreas(areas, scale).map(({ area, band }) => {
    const deliveryMode = deliveryModeFor(area, band);
    return { area: area.name || '', band, priority: priorityFor(area, band), deliveryMode, action: deliveryLabel(deliveryMode) };
  });
  return rows.sort((a, b) => (
    (PRIORITY_RANK[b.priority || 'low'] - PRIORITY_RANK[a.priority || 'low'])
    || (BAND_RANK[/** @type {Band} */ (b.band)] - BAND_RANK[/** @type {Band} */ (a.band)])
  ));
}

/**
 * Last page: one row per rated area, in the order they were given.
 *
 * @param {Area[]} areas
 * @param {string} scale
 */
function buildOutlookRows(areas, scale) {
  return ratedAreas(areas, scale).map(({ area, band }) => {
    const deliveryMode = deliveryModeFor(area, band);
    return {
      area: area.name || '',
      band,
      deliveryMode,
      deliveryModeLabel: deliveryLabel(deliveryMode),
      outlookYear: area.outlookYear ?? null,
    };
  });
}

/**
 * The five most frequent tagged defects across the visit. An assessor-edited
 * list wins verbatim.
 *
 * @param {Area[]} areas
 * @param {string[] | null} [override]
 * @returns {{id?: string, label: string, count?: number}[]}
 */
function aggregateKeyRisks(areas, override) {
  if (Array.isArray(override) && override.length) return override.map((label) => ({ label }));
  /** @type {Record<string, number>} */
  const counts = {};
  for (const area of areas || []) {
    for (const item of (area && area.items) || []) {
      for (const id of item.defects || []) counts[id] = (counts[id] || 0) + 1;
    }
  }
  return Object.keys(counts)
    .map((id) => ({ id, label: DEFECT_BY_ID[id] ? DEFECT_BY_ID[id].label : id, count: counts[id] }))
    .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id))
    .slice(0, 5);
}

/**
 * Defects per band, counted by AREA (once per area, in the area's roll-up band).
 *
 * @param {Area[]} areas
 * @param {string} scale
 */
function aggregateDefectsByBand(areas, scale) {
  /** @type {Record<Band, Record<string, number>>} */
  const acc = { poor: {}, fair: {}, good: {} };
  for (const { area, band } of ratedAreas(areas, scale)) {
    const seen = new Set();
    for (const item of area.items || []) {
      for (const id of item.defects || []) {
        if (seen.has(id)) continue;
        seen.add(id);
        const tier = acc[/** @type {Band} */ (band)];
        tier[id] = (tier[id] || 0) + 1;
      }
    }
  }
  /** @param {Record<string, number>} m */
  const toList = (m) => Object.keys(m)
    .map((id) => ({ id, label: DEFECT_BY_ID[id] ? DEFECT_BY_ID[id].label : id, count: m[id] }))
    .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
  return { poor: toList(acc.poor), fair: toList(acc.fair), good: toList(acc.good) };
}

/**
 * Pages 3+: rated areas, each with its rated-or-noted items as cards. A stored
 * note is used as written; otherwise the phrase library writes one. Photos are
 * the item's own, primary first, de-duplicated by url.
 *
 * @param {Area[]} areas
 * @param {string} scale
 */
function buildRegister(areas, scale) {
  return ratedAreas(areas, scale).map(({ area, band: areaBand }) => {
    const rows = (area.items || [])
      .filter((item) => itemBand(item, scale) || (item.note && item.note.trim()))
      .map((item) => {
        const band = itemBand(item, scale);
        const deliveryMode = band ? deliveryModeFor(area, band) : null;
        const photos = [...(item.photos || [])]
          .filter((p) => p && p.url)
          .sort((x, y) => Number(y.isPrimary === true) - Number(x.isPrimary === true))
          .filter((p, i, all) => all.findIndex((q) => q.url === p.url) === i)
          .map((p) => ({ url: p.url }));
        return {
          substrate: item.substrate,
          category: familyForSubstrate(item.substrate, item.baseSurface, area.side),
          band,
          rating: item.rating ?? null,
          ratingLabel: ratingLabel(scale, item.rating),
          note: (item.note && item.note.trim()) || generateConditionNote(item.substrate, { ...item, band }),
          deliveryMode,
          action: deliveryLabel(deliveryMode),
          priority: band ? priorityFor(area, band) : null,
          photos,
        };
      });
    return { area: area.name || '', band: areaBand, rows };
  });
}

/**
 * Higgins Recommendation: rated areas grouped Prioritise → Consider → Defer,
 * with the defects found in each tier. Empty tiers are left out.
 *
 * @param {Area[]} areas
 * @param {string} scale
 */
function buildRecommendationTiers(areas, scale) {
  /** @type {Record<Band, string[]>} */
  const byBand = { poor: [], fair: [], good: [] };
  for (const { area, band } of ratedAreas(areas, scale)) byBand[/** @type {Band} */ (band)].push(area.name || '');
  const defects = aggregateDefectsByBand(areas, scale);
  return /** @type {Band[]} */ (['poor', 'fair', 'good'])
    .filter((band) => byBand[band].length)
    .map((band) => ({
      band,
      ...BAND_RECOMMENDATION[band],
      areas: byBand[band],
      identified: defects[band].slice(0, 5).map((d) => d.label),
    }));
}

module.exports = {
  itemBand,
  computeStats,
  computeAreaStats,
  rollupAreaBand,
  overallBand,
  itemsNotNeedingRepaint,
  defaultDeliveryMode,
  defaultPriority,
  buildDashboardRows,
  buildOutlookRows,
  aggregateKeyRisks,
  aggregateDefectsByBand,
  buildRegister,
  buildRecommendationTiers,
};
